import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import Size from "../../models/size";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import SizeDeleteModal from "./SizeDeleteModal";
import SizeUpdateModal from "./SizeUpdateModal";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  SIZES_CREATE,
  SIZES_DESTROY,
  SIZES_UPDATE,
} from "../../auth/permissions";

const ITEMS_PER_PAGE = 10;

const SizeList = () => {
  const fields = [
    {
      key: "size",
      _classes: ["text-center"],
      label: "Nombre",
    },
    {
      key: "isKidSize",
      _classes: ["text-center"],
      label: "Adulto/Niños",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [showSizeEditModal, setShowSizeEditModal] = useState(false);
  const [showSizeDeleteModal, setShowSizeDeleteModal] = useState(false);

  const [size, setSize] = useState<Size | undefined>(undefined);
  const [search, setSearch] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchSizes = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const sizesStatus = await getList<Size>(
      "/sizes/",
      limit,
      offset,
      urlParams
    );
    if (sizesStatus.status === SUCCESS) {
      if (sizesStatus.data !== undefined) {
        const count = sizesStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setSizes(sizesStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = sizesStatus.detail
        ? sizesStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowSizeEditModal(true);
  };

  const onUpdateClick = (size: Size) => {
    setSize(size);
    setShowSizeEditModal(true);
  };

  const onDeleteClick = (size: Size) => {
    setSize(size);
    setShowSizeDeleteModal(true);
  };

  const onSizeEditCancel = () => {
    setSize(undefined);
    setShowSizeEditModal(false);
  };

  const onSizeEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowSizeEditModal(false);
    setSizes([]);
    setLoading(true);
    fetchSizes();
  };

  const onSizeDeleteCancel = () => {
    setSize(undefined);
    setShowSizeDeleteModal(false);
  };

  const onSizeDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowSizeDeleteModal(false);
    setSizes([]);
    setLoading(true);
    fetchSizes();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchSizes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("sizes")) {
        return;
      }
      fetchSizes();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Talles</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent permissionName={SIZES_CREATE}>
                  <CButton
                    size="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      size="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={sizes}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    actions: (item: Size) => {
                      const editButton = (
                        <PermissionRequiredComponent
                          permissionName={SIZES_UPDATE}
                        >
                          <CButton
                            className="text-white"
                            size="warning"
                            onClick={() => {
                              onUpdateClick(item);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </CButton>
                        </PermissionRequiredComponent>
                      );
                      const deleteButton = (
                        <PermissionRequiredComponent
                          permissionName={SIZES_DESTROY}
                        >
                          <CButton
                            className="text-white"
                            size="danger"
                            onClick={() => {
                              onDeleteClick(item);
                            }}
                          >
                            <i className="fa fa-trash"></i>
                          </CButton>
                        </PermissionRequiredComponent>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            {editButton}
                            {deleteButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                    isKidSize: (item: Size) => {
                      if (item.isKidSize) {
                        return <td className="text-center">Niños</td>;
                      }
                      return <td className="text-center">Adultos</td>;
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <SizeUpdateModal
        show={showSizeEditModal}
        onCancel={onSizeEditCancel}
        onSuccess={onSizeEditSuccess}
        size={size}
      />
      <SizeDeleteModal
        show={showSizeDeleteModal}
        onCancel={onSizeDeleteCancel}
        onSuccess={onSizeDeleteSuccess}
        size={size}
      />
    </>
  );
};

export default SizeList;
